import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import type { Locale } from '@/locales';

export const useLanguage = () => {
  const { i18n, t } = useTranslation();

  const currentLanguage = i18n.language as Locale;

  const changeLanguage = useCallback((language: Locale) => {
    // i18n.changeLanguage 会自动触发 languageChanged 事件
    // 该事件的监听器会处理 localStorage 和 document.documentElement.lang 的更新
    i18n.changeLanguage(language);
  }, [i18n]);

  const getSupportedLanguages = useCallback((): { code: Locale; name: string; nativeName: string }[] => [
    { code: 'zh-CN', name: 'Simplified Chinese', nativeName: '简体中文' },
    { code: 'zh-TW', name: 'Traditional Chinese', nativeName: '繁體中文' },
    { code: 'en', name: 'English', nativeName: 'English' },
  ], []);

  const getLanguageName = useCallback((code: Locale): string => {
    const languages = getSupportedLanguages();
    return languages.find(lang => lang.code === code)?.nativeName || code;
  }, [getSupportedLanguages]);

  const isRTL = useCallback((): boolean => {
    // 如果将来需要支持阿拉伯语等从右到左的语言
    return false;
  }, []);

  // 确保页面加载时设置正确的语言属性
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  return {
    currentLanguage,
    changeLanguage,
    getSupportedLanguages,
    getLanguageName,
    isRTL,
    t,
    // 兼容旧的API
    locale: currentLanguage,
    setLocale: changeLanguage,
  };
};